import React from "react";
import { Link } from "react-router-dom";
import { ASSETS, GURUKULA } from "../../lib/api";
import OrnamentalDivider from "./OrnamentalDivider";

export default function Hero() {
    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative overflow-hidden bg-ivory"
        >
            {/* Background texture grain */}
            <div
                className="absolute inset-0 opacity-[0.06] pointer-events-none mix-blend-multiply"
                style={{
                    backgroundImage:
                        "radial-gradient(rgba(90,17,31,0.6) 1px, transparent 1px)",
                    backgroundSize: "3px 3px",
                }}
            />

            <div className="relative max-w-5xl mx-auto px-6 lg:px-10 pt-20 pb-20 lg:pt-28 lg:pb-24 text-center">
                <div className="eyebrow mb-8" data-testid="hero-eyebrow">
                    · Hindustani Classical Tradition · Est. Bengaluru ·
                </div>

                {/* Centered 4" × 4" logo (≈ 384px) with ornamental layers */}
                <div
                    className="relative mx-auto fade-up-on-load"
                    style={{ width: "440px", height: "440px", maxWidth: "94vw", maxHeight: "94vw" }}
                    data-testid="hero-logo-wrap"
                >
                    {/* Outer rotating mandala ring */}
                    <svg
                        viewBox="0 0 200 200"
                        className="absolute inset-0 w-full h-full"
                        style={{ animation: "spin 60s linear infinite" }}
                        aria-hidden="true"
                    >
                        <defs>
                            <pattern id="petal-dots" patternUnits="userSpaceOnUse" width="4" height="4">
                                <circle cx="2" cy="2" r="0.6" fill="#B58A43" />
                            </pattern>
                        </defs>
                        <circle cx="100" cy="100" r="96" fill="none" stroke="#B58A43" strokeWidth="0.5" strokeDasharray="1 3" opacity="0.7" />
                        <circle cx="100" cy="100" r="92" fill="none" stroke="#5A111F" strokeWidth="0.3" opacity="0.6" />
                        {/* 24 ornamental petals */}
                        {Array.from({ length: 24 }).map((_, i) => {
                            const angle = (i * 360) / 24;
                            return (
                                <g key={i} transform={`rotate(${angle} 100 100)`}>
                                    <path
                                        d="M100 6 L102 14 L100 22 L98 14 Z"
                                        fill="#B58A43"
                                        opacity="0.7"
                                    />
                                </g>
                            );
                        })}
                    </svg>

                    {/* Counter-rotating inner ring */}
                    <svg
                        viewBox="0 0 200 200"
                        className="absolute inset-[6%] w-[88%] h-[88%]"
                        style={{ animation: "spin 90s linear infinite reverse" }}
                        aria-hidden="true"
                    >
                        <circle cx="100" cy="100" r="98" fill="none" stroke="#B58A43" strokeWidth="0.4" opacity="0.5" />
                        {Array.from({ length: 8 }).map((_, i) => {
                            const angle = (i * 360) / 8;
                            return (
                                <g key={i} transform={`rotate(${angle} 100 100)`}>
                                    <circle cx="100" cy="6" r="2" fill="#5A111F" opacity="0.7" />
                                    <path
                                        d="M100 14 Q102 18 100 22 Q98 18 100 14 Z"
                                        fill="#B58A43"
                                        opacity="0.6"
                                    />
                                </g>
                            );
                        })}
                    </svg>

                    {/* Multi-layered glow */}
                    <div className="absolute inset-[10%] rounded-full bg-gradient-to-br from-gold/35 via-gold/10 to-transparent blur-2xl" />
                    <div className="absolute inset-[18%] rounded-full bg-gold/15 blur-xl" />

                    {/* Logo image with ornamental frame */}
                    <div className="absolute inset-[12%] flex items-center justify-center">
                        <div className="relative w-full h-full">
                            <div className="absolute inset-0 rounded-full bg-ivory shadow-[0_20px_50px_-10px_rgba(90,17,31,0.35)]" />
                            <div className="absolute inset-[2%] rounded-full border border-gold/50" />
                            <div className="absolute inset-[5%] rounded-full border border-maroon/15" />
                            <img
                                src={ASSETS.logo}
                                alt="Shri Vishwa Bandhu Sangeetha Gurukula logo"
                                data-testid="hero-logo-image"
                                className="relative w-full h-full object-contain rounded-full p-[7%] animate-diya-glow"
                            />
                        </div>
                    </div>

                    {/* Corner ornaments */}
                    {[
                        { pos: "top-0 left-1/2 -translate-x-1/2 -translate-y-1/2" },
                        { pos: "bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2" },
                        { pos: "top-1/2 left-0 -translate-x-1/2 -translate-y-1/2" },
                        { pos: "top-1/2 right-0 translate-x-1/2 -translate-y-1/2" },
                    ].map((c, i) => (
                        <div key={i} className={`absolute ${c.pos} pointer-events-none`}>
                            <svg width="22" height="22" viewBox="0 0 22 22">
                                <path
                                    d="M11 1 L13 9 L21 11 L13 13 L11 21 L9 13 L1 11 L9 9 Z"
                                    fill="#B58A43"
                                />
                            </svg>
                        </div>
                    ))}
                </div>

                {/* Kannada Gurukula name — placed below the logo, ornamental */}
                <div className="mt-8 flex flex-col items-center" data-testid="hero-kannada-name">
                    <div className="flex items-center gap-4 text-gold" aria-hidden="true">
                        <span className="h-px w-12 sm:w-16 bg-gradient-to-r from-transparent via-gold to-transparent" />
                        <svg width="14" height="14" viewBox="0 0 28 28" className="text-gold">
                            <path d="M14 2 L17 11 L26 14 L17 17 L14 26 L11 17 L2 14 L11 11 Z" fill="currentColor" />
                        </svg>
                        <span className="h-px w-12 sm:w-16 bg-gradient-to-r from-transparent via-gold to-transparent" />
                    </div>
                    <h2
                        className="text-kannada font-semibold text-3xl sm:text-4xl lg:text-5xl text-maroon mt-4 leading-tight tracking-tight"
                        data-testid="hero-kannada-heading"
                    >
                        ಶ್ರೀ ವಿಶ್ವಬಂಧು ಸಂಗೀತ ಗುರುಕುಲ
                    </h2>
                </div>

                <h1
                    className="font-display text-5xl sm:text-6xl lg:text-7xl text-maroon mt-10 leading-[1.04] tracking-tight"
                    data-testid="hero-title"
                >
                    Where music <span className="italic">finds its soul,</span>
                    <br className="hidden sm:block" />
                    and dedication <span className="italic">finds its voice.</span>
                </h1>

                <div className="mt-9 max-w-2xl mx-auto">
                    <p
                        className="font-serif text-xl lg:text-2xl text-ink/85 leading-relaxed"
                        data-testid="hero-tagline-en"
                    >
                        “{GURUKULA.tagline_en}”
                    </p>
                    <p
                        className="text-kannada text-2xl lg:text-3xl text-maroon/90 mt-5 leading-relaxed"
                        data-testid="hero-tagline-kn"
                    >
                        “{GURUKULA.tagline_kn}”
                    </p>
                </div>

                <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
                    <Link to="/register" className="btn-primary" data-testid="hero-cta-enroll">
                        Begin Your Sadhana
                    </Link>
                    <a href="#courses" className="btn-ghost-gold" data-testid="hero-cta-courses">
                        Explore Courses
                    </a>
                </div>

                <div className="mt-14 flex flex-wrap items-center justify-center gap-x-10 gap-y-6 text-[13px] tracking-[0.22em] uppercase text-inkmuted font-sans">
                    <div className="text-center">
                        <span className="block text-maroon font-display text-3xl normal-case tracking-normal">
                            25+ yrs
                        </span>
                        Guru-Shishya Tradition
                    </div>
                    <div className="hidden sm:block h-10 w-px bg-gold-100" />
                    <div className="text-center">
                        <span className="block text-maroon font-display text-3xl normal-case tracking-normal">
                            500+
                        </span>
                        Students Trained
                    </div>
                    <div className="hidden sm:block h-10 w-px bg-gold-100" />
                    <div className="text-center">
                        <span className="block text-maroon font-display text-2xl normal-case tracking-normal">
                            Online · Offline
                        </span>
                        Worldwide reach
                    </div>
                </div>
            </div>

            {/* Poster strip — moved below so the logo holds the spotlight */}
            <div className="relative max-w-6xl mx-auto px-6 lg:px-10 pb-20 lg:pb-24">
                <div className="relative">
                    <div className="absolute -inset-3 rounded-sm border border-gold/40 pointer-events-none" />
                    <div className="absolute -inset-1 rounded-sm border border-gold/20 pointer-events-none" />
                    <img
                        src={ASSETS.poster}
                        alt="Shri Vishwa Bandhu Sangeetha Gurukula — Shri Ananya Bhargava Bedur"
                        className="relative w-full h-auto object-cover shadow-[0_30px_60px_-20px_rgba(90,17,31,0.45)]"
                        data-testid="hero-poster-image"
                    />
                </div>
            </div>

            <OrnamentalDivider className="pb-14" />
        </section>
    );
}
